package com.daytimegaming.ourdailyfinances.presentation.home

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.daytimegaming.ourdailyfinances.domain.Response
import com.daytimegaming.ourdailyfinances.domain.model.RecurringTransactionStream
import com.daytimegaming.ourdailyfinances.domain.model.Transaction
import com.daytimegaming.ourdailyfinances.domain.usecase.DashboardUseCase
import com.daytimegaming.ourdailyfinances.domain.repository.TransactionRepository
import kotlinx.coroutines.ExperimentalCoroutinesApi
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.flatMapLatest
import kotlinx.coroutines.flow.flowOf
import kotlinx.coroutines.launch

sealed class RecurringTransactionDetailScreenState {
    data object Loading : RecurringTransactionDetailScreenState()
    data class Error(val message: String) : RecurringTransactionDetailScreenState()
    data class Loaded(
        val stream: RecurringTransactionStream,
        val transactions: List<Transaction>,
        val transactionDashboardIds: Map<String, String?> = emptyMap()
    ) : RecurringTransactionDetailScreenState()
}

@OptIn(ExperimentalCoroutinesApi::class)
class RecurringTransactionDetailViewModel(
    private val streamId: String,
    private val dashboardUseCase: DashboardUseCase,
    private val transactionRepository: TransactionRepository
) : ViewModel() {

    private val _state = MutableStateFlow<RecurringTransactionDetailScreenState>(RecurringTransactionDetailScreenState.Loading)
    val state = _state.asStateFlow()

    init {
        load()
    }

    fun load() {
        viewModelScope.launch {
            if (_state.value !is RecurringTransactionDetailScreenState.Loaded) {
                _state.value = RecurringTransactionDetailScreenState.Loading
            }
            transactionRepository.refreshRecurringTransactions()
        }

        viewModelScope.launch {
            val dashboardsDetailsFlow = dashboardUseCase.GetDashboards().flatMapLatest { dashboardsResponse ->
                when (dashboardsResponse) {
                    is Response.Error -> flowOf(Response.Error(dashboardsResponse.message))
                    Response.Loading -> flowOf(Response.Loading)
                    is Response.Success -> {
                        val dashboards = dashboardsResponse.data
                        if (dashboards.isEmpty()) {
                            flowOf(Response.Success(emptyList()))
                        } else {
                            val detailFlows = dashboards.map { dashboard ->
                                dashboardUseCase.GetDashboardDetail(dashboard.dashboardId)
                            }
                            combine(detailFlows) { detailsResponses ->
                                val error = detailsResponses.filterIsInstance<Response.Error>().firstOrNull()
                                if (error != null) {
                                    Response.Error(error.message)
                                } else if (detailsResponses.any { it is Response.Loading }) {
                                    Response.Loading
                                } else {
                                    val details = detailsResponses.filterIsInstance<Response.Success<com.daytimegaming.ourdailyfinances.domain.model.DashboardDetail>>().map { it.data }
                                    Response.Success(details)
                                }
                            }
                        }
                    }
                }
            }

            combine(
                transactionRepository.recurringTransactions,
                dashboardsDetailsFlow
            ) { recurringResponse, detailsResponse ->
                when {
                    recurringResponse is Response.Error -> {
                        RecurringTransactionDetailScreenState.Error(recurringResponse.message)
                    }
                    detailsResponse is Response.Error -> {
                        RecurringTransactionDetailScreenState.Error(detailsResponse.message)
                    }
                    recurringResponse is Response.Loading || detailsResponse is Response.Loading -> {
                        if (_state.value is RecurringTransactionDetailScreenState.Loaded) {
                            _state.value
                        } else {
                            RecurringTransactionDetailScreenState.Loading
                        }
                    }
                    recurringResponse is Response.Success && detailsResponse is Response.Success -> {
                        val stream = recurringResponse.data.find { it.streamId == streamId }
                        if (stream != null) {
                            val allDashboardTransactions = detailsResponse.data
                                .flatMap { it.transactions }
                                .distinctBy { it.transactionId }
                            
                            val transactionDashboardIds = mutableMapOf<String, String?>()
                            detailsResponse.data.forEach { dashboardDetail ->
                                dashboardDetail.transactions.forEach { tx ->
                                    transactionDashboardIds[tx.transactionId] = dashboardDetail.dashboardId
                                }
                            }
                            
                            val associatedTransactions = allDashboardTransactions.filter { tx ->
                                tx.transactionId in stream.transactionIds
                            }
                            
                            RecurringTransactionDetailScreenState.Loaded(
                                stream = stream,
                                transactions = associatedTransactions,
                                transactionDashboardIds = transactionDashboardIds
                            )
                        } else {
                            RecurringTransactionDetailScreenState.Error("Subscription stream not found.")
                        }
                    }
                    else -> {
                        _state.value
                    }
                }
            }.collect { newState ->
                _state.value = newState
            }
        }
    }
}
